import { LanguageCode } from 'i18n/internationalization.types';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useCreateCustomTemplateMutation from 'components/domain/automation/customTemplates/useCreateCustomTemplateMutation';
import useUpdateCustomTemplateMutation from 'components/domain/automation/customTemplates/useUpdateCustomTemplateMutation';
import {
  CustomTemplateCreatePayload,
  CustomTemplateLocales,
  CustomTemplateUpdatePayload,
  MessagingType,
} from 'models/automation/CustomTemplates';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { useTranslation } from 'react-i18next';
import useModalId from 'components/modal/useModalId';
import useAppModal from 'hooks/useAppModal';
import { typedKeys } from 'utils/funcs';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { CUSTOM_TEMPLATES_BASE_QUERY_KEY } from 'components/domain/automation/customTemplates/customTemplatesQuery';
import { FormContextParts } from 'components/forms/form/Form.types';
import { getAvailableI18nLanguage } from 'utils/languageUtils';
import { CustomTemplateAddEditFormValues } from './CustomTemplateAddEditModal.schema';
import useSaveConfirmationModal from './confirmSave/useSaveConfirmationModal';

const useCustomTemplateFormSubmit = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const modalId = useModalId();
  const { closeModal } = useAppModal();
  const { notifyError, notifySuccess } = useNotify();
  const { mutateAsync: createCustomTemplate } =
    useCreateCustomTemplateMutation();
  const { mutateAsync: updateCustomTemplate } =
    useUpdateCustomTemplateMutation();
  const { openSaveConfirmationModal } = useSaveConfirmationModal();

  const onError = (error: BaseErrorResponse) => {
    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.customTemplates.apiErrorCodes.',
        t,
      }),
    );
  };

  const onSuccess = () => {
    closeModal(modalId);
    navigate(-1);
  };

  const handleSubmit = async (
    formValues: CustomTemplateAddEditFormValues,
    _: unknown,
    {
      formState: { dirtyFields },
    }: FormContextParts<CustomTemplateAddEditFormValues>,
  ) => {
    const locales = typedKeys(dirtyFields.locales || {}).reduce(
      (acc, language) => {
        const languageCode = getAvailableI18nLanguage(language) as LanguageCode;

        const { subject, content_htmlContent } =
          formValues.locales?.[language] ?? {};

        if (
          (!subject && formValues.messagingType === MessagingType.EMAIL) ||
          !content_htmlContent
        ) {
          return acc;
        }

        acc[languageCode] = {
          ...(subject ? { subject } : {}),
          content: content_htmlContent,
        };

        return acc;
      },
      {} as CustomTemplateLocales,
    );

    if (formValues.uid) {
      const payload: CustomTemplateUpdatePayload = {
        customTemplateUid: formValues.uid,
        name: formValues.name,
        locales,
      };

      openSaveConfirmationModal({
        customTemplateName: formValues.name,
        onAfterConfirm: async () => {
          await updateCustomTemplate(payload, {
            onSuccess: () => {
              onSuccess();
              notifySuccess(
                t('pageAutomations.customTemplates.modal.updateSuccess'),
              );
            },
            onError: (error) => {
              onError(error as BaseErrorResponse);
            },
            onSettled: () => {
              queryClient.invalidateQueries([CUSTOM_TEMPLATES_BASE_QUERY_KEY]);
            },
          });
        },
      });
    } else {
      const payload: CustomTemplateCreatePayload = {
        name: formValues.name,
        messagingType: formValues.messagingType,
        locales,
      };

      try {
        await createCustomTemplate(payload, {
          onSuccess: () => {
            onSuccess();
            notifySuccess(
              t('pageAutomations.customTemplates.modal.createSuccess'),
            );
          },
        });
      } catch (error) {
        onError(error as BaseErrorResponse);
      }

      queryClient.invalidateQueries([CUSTOM_TEMPLATES_BASE_QUERY_KEY]);
    }
  };

  return {
    handleSubmit,
  };
};

export default useCustomTemplateFormSubmit;
